import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { Star, Check, Zap, ShieldCheck, Headphones, ArrowLeft, MessageSquare } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

export default function ProductPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form, setForm] = useState({ user_name: "", rating: 5, body: "", title: "" });
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      const best = r.data.plans?.find((pl) => pl.badge === "Best Value") || r.data.plans?.[0];
      setSelectedPlan(best);
      api.get("/reviews", { params: { product_id: r.data.id } }).then((rr) => setReviews(rr.data));
    });
  }, [slug]);

  if (!p) return <div className="max-w-7xl mx-auto px-5 py-20 text-kyro-mute">Loading...</div>;

  const submitReview = async (e) => {
    e.preventDefault();
    if (!form.user_name || !form.body) return toast.error("Name & review body required");
    try {
      await api.post("/reviews", { product_id: p.id, ...form });
      toast.success("Thanks for your review!");
      setForm({ user_name: "", rating: 5, body: "", title: "" });
      const rr = await api.get("/reviews", { params: { product_id: p.id } });
      setReviews(rr.data);
    } catch { toast.error("Failed to submit review"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-kyro-mute hover:text-kyro-ink mb-8"><ArrowLeft className="w-4 h-4" /> Back to shop</Link>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Left: image */}
        <div className="lg:col-span-2">
          <div className="kyro-card overflow-hidden aspect-[4/3] bg-white/5">
            {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {[Zap, ShieldCheck, Headphones].map((Icon, i) => (
              <div key={i} className="kyro-card p-3 text-center">
                <Icon className="w-4 h-4 text-[#FF4433] mx-auto mb-1" />
                <p className="text-[10px] uppercase tracking-wider text-kyro-mute">{["1-2 hrs", "Verified", "24/7 Support"][i]}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right: info */}
        <div className="lg:col-span-3">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs uppercase tracking-widest text-kyro-faint">{p.brand}</span>
            {p.is_bestseller && <span className="text-[10px] uppercase tracking-widest font-bold text-[#FF4433]">Best Seller</span>}
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">{p.name}</h1>
          <div className="flex items-center gap-4 mt-4">
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-4 h-4 ${i < Math.round(p.rating) ? "fill-[#FF4433] text-[#FF4433]" : "text-white/20"}`} />
              ))}
            </div>
            <span className="text-sm text-kyro-mute">{p.rating?.toFixed(1)} • {p.review_count} reviews</span>
          </div>
          <p className="mt-6 text-lg text-kyro-mute leading-relaxed">{p.description}</p>

          {p.features?.length > 0 && (
            <div className="mt-6 grid grid-cols-2 gap-3">
              {p.features.map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#FF4433] mt-0.5 shrink-0" />
                  <span className="text-kyro-mute">{f}</span>
                </div>
              ))}
            </div>
          )}

          {/* Plans */}
          <div className="mt-10">
            <div className="kyro-overline mb-4">Choose your plan</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {p.plans?.map((pl) => {
                const active = selectedPlan?.label === pl.label;
                const savings = pl.original_price ? Math.round(((pl.original_price - pl.price) / pl.original_price) * 100) : 0;
                return (
                  <button
                    key={pl.label}
                    data-testid={`plan-${pl.duration_months}m`}
                    onClick={() => setSelectedPlan(pl)}
                    className={`relative text-left rounded-2xl p-5 border transition-colors ${active ? "bg-[#FF4433]/10 border-[#FF4433]" : "border-white/10 hover:border-white/25"}`}
                  >
                    {pl.badge && (
                      <span className="absolute -top-2 left-4 text-[10px] font-bold uppercase tracking-widest bg-[#FF4433] text-white px-2 py-0.5 rounded-full">{pl.badge}</span>
                    )}
                    <div className="text-xs text-kyro-mute uppercase tracking-widest">{pl.label}</div>
                    <div className="font-display font-black text-2xl mt-2">₹{pl.price.toFixed(0)}</div>
                    {pl.original_price && (
                      <div className="text-xs text-kyro-faint line-through">₹{pl.original_price.toFixed(0)}</div>
                    )}
                    {savings > 0 && <div className="text-[10px] text-[#FF4433] mt-1 font-bold">Save {savings}%</div>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <button
              data-testid="add-to-cart-btn"
              onClick={() => { if (selectedPlan) { add(p, selectedPlan); toast.success("Added to cart"); } }}
              className="kyro-btn-primary"
            >
              Add to cart — ₹{selectedPlan?.price?.toFixed(0)}
            </button>
            <Link to="/checkout" onClick={() => { if (selectedPlan) add(p, selectedPlan); }} className="kyro-btn-secondary" data-testid="buy-now-btn">Buy now</Link>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="kyro-overline">Reviews</div>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter mt-2">What buyers say</h2>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 mb-10">
          {reviews.length === 0 && <p className="text-kyro-mute col-span-3">No reviews yet. Be the first!</p>}
          {reviews.slice(0, 6).map((r) => (
            <motion.div key={r.id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="kyro-card p-6">
              <div className="flex gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-[#FF4433] text-[#FF4433]" : "text-white/15"}`} />
                ))}
              </div>
              {r.title && <p className="font-semibold text-sm mb-1">{r.title}</p>}
              <p className="text-sm text-kyro-mute leading-relaxed">{r.body}</p>
              <p className="text-xs text-kyro-faint mt-4">— {r.user_name}</p>
            </motion.div>
          ))}
        </div>

        <form onSubmit={submitReview} className="kyro-card p-6 md:p-8 max-w-2xl">
          <div className="flex items-center gap-2 mb-4"><MessageSquare className="w-4 h-4 text-[#FF4433]" /><span className="kyro-overline">Write a review</span></div>
          <input data-testid="review-name" placeholder="Your name" value={form.user_name} onChange={(e) => setForm({ ...form, user_name: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 mb-3 outline-none focus:border-[#FF4433]" />
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm text-kyro-mute mr-2">Rating:</span>
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })}>
                <Star className={`w-5 h-5 ${n <= form.rating ? "fill-[#FF4433] text-[#FF4433]" : "text-white/20"}`} />
              </button>
            ))}
          </div>
          <textarea data-testid="review-body" placeholder="Share your experience..." value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 mb-4 outline-none focus:border-[#FF4433]" />
          <button data-testid="submit-review-btn" className="kyro-btn-primary text-sm">Post review</button>
        </form>
      </section>
    </div>
  );
}
